import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import {
  filterNavByRole,
  MAIN_NAV_ITEMS,
  SETTINGS_NAV_ITEMS,
  type NavItem,
} from './navItems';

type AppNavLinksProps = {
  collapsed?: boolean;
  onNavigate?: () => void;
  className?: string;
};

function NavLinkButton({
  item,
  active,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const Icon = item.icon;

  const button = (
    <Button
      variant={active ? 'default' : 'ghost'}
      className={cn(
        'w-full justify-start',
        collapsed && 'justify-center',
        active && 'bg-payroll-primary text-white hover:bg-payroll-primary/90'
      )}
      asChild
    >
      <Link to={item.href} onClick={onNavigate}>
        <Icon className="h-4 w-4 shrink-0" />
        {!collapsed && <span className="ml-2">{item.label}</span>}
      </Link>
    </Button>
  );

  if (!collapsed) {
    return button;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent side="right">
          <p>{item.label}</p>
          <p className="text-xs text-muted-foreground">{item.description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function AppNavLinks({
  collapsed = false,
  onNavigate,
  className,
}: AppNavLinksProps) {
  const { pathname } = useLocation();
  const { user } = useAuth();

  const mainRoutes = filterNavByRole(MAIN_NAV_ITEMS, user?.role);
  const settingsRoutes = filterNavByRole(SETTINGS_NAV_ITEMS, user?.role);
  const showSettings = settingsRoutes.length > 0;

  const isActive = (href: string) => pathname === href;

  return (
    <div className={cn('space-y-1', className)}>
      <div className="space-y-1 py-2">
        {!collapsed && (
          <p className="text-xs font-semibold text-gray-500 px-4 mb-2">MAIN MENU</p>
        )}
        {mainRoutes.map((route) => (
          <NavLinkButton
            key={route.href}
            item={route}
            active={isActive(route.href)}
            collapsed={collapsed}
            onNavigate={onNavigate}
          />
        ))}
      </div>

      {showSettings && (
        <>
          <Separator className="my-4" />
          <div className="space-y-1 py-2">
            {!collapsed && (
              <p className="text-xs font-semibold text-gray-500 px-4 mb-2">SETTINGS</p>
            )}
            {settingsRoutes.map((route) => (
              <NavLinkButton
                key={route.href}
                item={route}
                active={isActive(route.href)}
                collapsed={collapsed}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
